import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Create,
  ReadMore,
  Person,
  Room,
  ExpandMore,
  CalendarToday,
} from '@mui/icons-material'
import {
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'

import { HeadCell } from 'components/molecules/DataTable/TableHeader'

import useGetLocationsTree from 'lib/services/api/product-service/getLocationsTree'
import { extractTrayJSONData } from 'lib/utils/extractTrayJSONData'
import { useTrayManagementContext } from './TrayManagement.context'

import { MappedTrays } from 'views/TrayManagementLayout/TrayManagement/TrayManagement.types'
import { InventoryLocation } from 'views/LocationManagement/LocationManagement.types'
import {
  TrayStatus,
  TrayStatusDetails,
} from 'components/organisms/EditTrayModal/EditTrayModal.types'
import { SPDContextProps } from 'views/SPDLayout/SPD/SPD.types'
import { ItemResponse, Procedures } from 'common/types'
import { formatSurgeryProviderName } from 'lib/utils/formatSurgeryProviderName'
import { useUpdateTrayItem } from 'lib/apollo/hooks'
import { useUser } from 'app/User'
import dayjs from 'lib/dayjs'
import classNames from 'classnames'
import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { getTrayStatusDisplay } from 'utils/getTrayStatusDisplay'

/**
 * Custom hook for managing tray logic in a tray management feature.
 * Provides state and functions to handle tray operations.
 * @returns Object containing state variables and functions for tray management.
 */
const useTrayManagementLogic = () => {
  const navigate = useNavigate()
  const flags = useFlags()
  const { data, loading: isLoadingLocations } = useGetLocationsTree()
  const {
    setSelectedTrayCategory,
    setSelectedTray,
    selectedTray,
    isListOfSurgeriesOpen,
    setIsListOfSurgeriesOpen,
    isAddTrayModalOpen,
    setIsAddTrayModalOpen,
    isEditTrayStatusModalOpen,
    setIsEditTrayStatusModalOpen,
    trays,
    setTrays,
    lastViewedTrayId,
    setLastViewedTrayId,
    refetch,
  } = useTrayManagementContext()
  const { editTrayItem } = useUpdateTrayItem()
  const { user } = useUser()
  const { setTrayData } = useSPD()

  const [locations, setLocations] = useState<InventoryLocation[]>([])
  const [locationsWithParents, setLocationsWithParents] = useState<
    InventoryLocation[]
  >([])
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false)
  const [isRejectionDetailsModalOpen, setIsRejectionDetailsModalOpen] =
    useState<boolean>(false)
  const [rejectionDetails, setRejectionDetails] =
    useState<TrayStatusDetails | null>(null)
  const [
    isShowChooseTrayLocationModalVisible,
    setIsShowChooseTrayLocationModalVisible,
  ] = useState<boolean>(false)
  const [expandedCases, setExpandedCases] = useState<Set<string>>(new Set())
  const [isUpdatingTrayStatus, setIsUpdatingTrayStatus] =
    useState<boolean>(false)

  const selectedTrayCategory = selectedTray
    ? extractTrayJSONData(selectedTray.trayItem)?.trayCategory
    : undefined
  const isSPD = user?.roles?.includes('MGAT_SPD')
  const isRep = user?.roles?.includes('MGAT_REP')
  const isNurse =
    user?.roles?.includes('MGAT_NURSE') && !user?.roles?.includes('MGAT_SPD')

  /**
   * Defines the header cells for the tray management table.
   */
  const trayTableHeaderCells: HeadCell[] = [
    { id: 'trayIcon', label: '', numeric: false },
    { id: 'trayID', label: 'Tray Id', numeric: false },
    { id: 'vendor', label: 'Vendor', numeric: false },
    { id: 'createdAt', label: 'Created At', numeric: false },
    { id: 'case', label: 'Case', numeric: false },
    { id: 'status', label: 'Status', numeric: false },
    { id: 'location', label: 'Location', numeric: false },
    { id: 'trayDetails', label: 'Tray Details', numeric: false },
  ]

  const scrollToTray = useCallback(
    (trayId: string) => {
      if (!trayId) return

      const tray = trays.find((t) => t.barcode === trayId)
      if (tray) {
        const trayDetails = extractTrayJSONData(tray)
        const surgeryId = trayDetails?.surgeryId
        const surgery = trayDetails?.surgeryDetails
        const hasCaseNumber = surgery?.surgicalCase?.number

        if (surgeryId && (hasCaseNumber || surgery?._id)) {
          const caseKey = `${surgeryId}-${hasCaseNumber || surgery?._id}`
          if (!expandedCases.has(caseKey)) {
            setExpandedCases((prev) => {
              const newSet = new Set(prev)
              newSet.add(caseKey)
              return newSet
            })
          }
        }

        setTimeout(() => {
          const trayElement = document.getElementById(`tray-card-${trayId}`)
          if (trayElement) {
            trayElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            trayElement.classList.add('highlight-tray')
            setTimeout(() => {
              trayElement.classList.remove('highlight-tray')
            }, 2000)
          }
        }, 100)
      }
    },
    [trays, expandedCases]
  )

  useEffect(() => {
    if (lastViewedTrayId) {
      scrollToTray(lastViewedTrayId)
      setLastViewedTrayId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastViewedTrayId, scrollToTray])

  /**
   * Redirects the user to the SPD workflow so they can start loading their tray immediately
   */
  const handleLoadTray = (selectedTray: ItemResponse) => {
    const trayData = {
      trayID: selectedTray.barcode || '',
      trayItem: selectedTray,
    }

    const encodedTrayData = Buffer.from(JSON.stringify(trayData)).toString(
      'base64'
    )

    navigate(`/spd?trayData=${encodedTrayData}`)
  }

  const handleChooseTrayLocationModalOpen = (tray: ItemResponse) => {
    setIsShowChooseTrayLocationModalVisible(true)
    setTrayData({
      trayID: `${tray.id}`,
      trayItem: tray,
    })
  }

  const handleChooseTrayLocationModalClose = () => {
    setIsShowChooseTrayLocationModalVisible(false)
    setTrayData(null)
  }

  /**
   * Handles redirecting to the AddTray workflow to fix a rejected tray
   * @param tray The rejected tray that needs to be fixed
   */
  const handleFixRejectedTray = (tray: ItemResponse) => {
    // Encode the tray data to pass it to the AddTray workflow
    const trayData = {
      trayID: tray.barcode || '',
      trayItem: tray,
      isRejectedTrayFix: true,
    }

    const encodedTrayData = Buffer.from(JSON.stringify(trayData)).toString(
      'base64'
    )
    navigate(`./add-tray?rejectedTrayData=${encodedTrayData}`)
  }

  /**
   * Maps tray data into structured cells for the tray management table body.
   * @param trays Array of tray items to be mapped into table rows.
   * @returns Mapped tray data formatted for table display.
   */
  const trayTableBodyCells = (trays: ItemResponse[]): MappedTrays[] => {
    return trays.map((tray) => {
      const trayDetails = extractTrayJSONData(tray)
      const surgery = trayDetails?.surgeryDetails
      const isAssigned = trayDetails?.surgeryId && trayDetails?.surgeryDetails
      const hasCaseNumber = surgery?.surgicalCase?.number
      let isUnassigned: boolean = true

      if (trayDetails?.status !== 'unAssigned') {
        if (hasCaseNumber || surgery?._id) {
          isUnassigned = false
        }
      }
      const shouldBeAllowedToEditTrayStatus =
        isSPD || (isRep && trayDetails?.status === 'readyToFill')
      const shouldBeAllowedToAssignToSurgery =
        trayDetails?.status !== 'rejected' &&
        !isRep &&
        (!(isNurse && isAssigned) || isSPD)
      const selectedSurgeon = trayDetails?.selectedSurgeon || 'Unassigned'
      const facilityTrayImg = require('assets/icons/images/facility-tray.jpg')
      const consignedTrayImg = require('assets/icons/images/consigned-tray.jpg')
      const loanerTrayImg = require('assets/icons/images/loaner-tray.jpg')
      const trayImg =
        trayDetails?.trayCategory === 'facility'
          ? facilityTrayImg
          : trayDetails?.trayCategory === 'consigned'
          ? consignedTrayImg
          : loanerTrayImg
      const trayStatusDisplay = getTrayStatusDisplay(trayDetails?.status)
      const trayStatusDisplayText =
        trayStatusDisplay === 'Unknown Status'
          ? !isUnassigned
            ? 'Assigned'
            : 'Unkown Status'
          : trayStatusDisplay
      const surgeryDateTime =
        surgery?.procedures && Array.isArray(surgery.procedures)
          ? surgery.procedures
              .map((proc: Procedures) => proc.dateTime)
              .filter(Boolean)
              .sort(
                (a: string, b: string) =>
                  new Date(a).getTime() - new Date(b).getTime()
              )[0]
          : null

      const isRejected = trayDetails?.status === 'rejected'
      const isReadyForPickup = trayDetails?.status === 'readyForPickup'
      const isRepNotified = isRejected || isReadyForPickup

      return {
        trayIcon: (
          <div
            className={`tray-icon-svg_container ${trayDetails?.trayCategory}`}
          >
            <img src={trayImg} alt="Tray Icon" className="tray-icon_svg" />
          </div>
        ),
        trayId: `${tray.barcode}`,
        vendor: `${tray.companyName}`,
        createdAt: `${dayjs(tray.createdAt).format('MM/DD/YYYY h:mm A')}`,
        case: (
          <div className="case-number_container">
            {shouldBeAllowedToAssignToSurgery && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  handleAssignToSurgeryClick(tray)
                }}
              >
                <Create />
              </Button>
            )}
            <Typography
              variant="body2"
              className={classNames({
                'un-assigned': isUnassigned,
                'case-information_wrapper': !isUnassigned,
              })}
            >
              {isUnassigned ? (
                <Typography variant="body2">Unassigned</Typography>
              ) : (
                <>
                  <Typography variant="body2">
                    - <b>Room:</b> {surgery?.visit?.location?.room}
                  </Typography>
                  {(isNurse || isSPD) && (
                    <>
                      <Typography variant="body2">
                        - <b>Patient:</b>
                        {`${
                          surgery?.patient?.lastName
                        }, ${surgery?.patient?.firstName?.charAt(0)}`}
                      </Typography>
                      <Typography variant="body2">
                        - <b>DOB:</b> {surgery?.patient?.dateOfBirth}
                      </Typography>
                      <Typography variant="body2">
                        - <b>MRN:</b> {surgery?.patient?.idMR}
                      </Typography>

                      <br />
                    </>
                  )}
                  {flags.procedureListSurgeryType && (
                    <Typography variant="body2">
                      - <b>Surgery:</b> {surgery?.procedures?.[0]?.description}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    - <b>Surgeon:</b>{' '}
                    {surgery && surgery.visit?.attendingProvider
                      ? formatSurgeryProviderName(surgery)
                      : selectedSurgeon}
                  </Typography>
                  <Typography variant="body2">
                    - <b>Date Of Service:</b>{' '}
                    {dayjs(surgeryDateTime).format('MM/DD/YYYY h:mm A')}
                  </Typography>
                </>
              )}
            </Typography>
          </div>
        ),
        status: (
          <Box>
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Chip
                label={trayStatusDisplay}
                className={`status-chip ${trayDetails?.status}`}
              />
              {isRejected && trayDetails?.statusDetails && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  sx={{ mt: 0.5 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleViewRejectionDetails(trayDetails?.statusDetails || {})
                  }}
                >
                  View Rejection Details
                </Button>
              )}
              {isRejected && isRep && (
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  sx={{ mt: 0.5 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleFixRejectedTray(tray)
                  }}
                >
                  Fix Rejected Tray
                </Button>
              )}
              {isRepNotified && (
                <Chip
                  label="Rep Notified ✅"
                  size="small"
                  className="rep-notified-chip"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
          </Box>
        ),
        location: isLoadingLocations ? (
          'Loading...'
        ) : (
          <div className="case-number_container">
            {(isSPD || isNurse) && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  handleChooseTrayLocationModalOpen(tray)
                }}
              >
                <Create />
              </Button>
            )}

            <Typography variant="body2">
              {findLocation(tray?.locationId as number)?.name}
            </Typography>
          </div>
        ),
        trayDetails: (
          <Button onClick={() => handleViewDetailsClick(tray)}>
            <ReadMore />
          </Button>
        ),
      }
    })
  }

  /**
   * Effect to update locations state when data changes.
   */
  useEffect(() => {
    if (data) {
      const { hospital } = data.getLocationsTree
      const allLocations = hospital.locations
      const locationsWithParents = allLocations.filter(
        (location) => location.parentLocationId
      )

      setLocationsWithParents(locationsWithParents)
      setLocations(allLocations)
    }
  }, [data])

  /**
   * Effect to open details modal when selected tray changes.
   */
  useEffect(() => {
    if (selectedTray && !isListOfSurgeriesOpen) {
      setIsDetailsModalOpen(true)
    }
  }, [selectedTray, isListOfSurgeriesOpen])

  /**
   * Opens the modal for adding a new tray.
   */
  const handleAddTrayModalOpen = () => {
    setIsAddTrayModalOpen(true)
  }

  /**
   * Redirects user to the add tray page.
   */
  const handleAddTrayClick = () => {
    navigate('/tray-management/add-tray')
  }

  /**
   * Closes the modal for adding a new tray.
   */
  const handleAddTrayModalClose = () => {
    setSelectedTrayCategory(null)
    setIsAddTrayModalOpen(false)
  }

  /**
   * Finds a location by its ID.
   * @param locationId The ID of the location to find.
   * @returns The location object if found, otherwise undefined.
   */
  const findLocation = (locationId: number): InventoryLocation | undefined => {
    return locations.find((location) => location.id === locationId)
  }

  /**
   * Closes the details modal for the selected tray.
   */
  const handleDetailsModalClose = () => {
    setSelectedTray(null)
    setIsDetailsModalOpen(false)
  }

  /**
   * Opens the list of surgeries modal and sets the selected tray.
   * @param selectedTray The tray to be assigned to a surgery.
   */
  const handleAssignToSurgeryClick = (tray: ItemResponse) => {
    const spdTray: SPDContextProps['trayData'] = {
      trayID: `${tray.id}`,
      trayItem: tray,
    }

    setIsListOfSurgeriesOpen(true)
    setIsDetailsModalOpen(false)
    setSelectedTray(spdTray)
  }

  /**
   * Closes the list of surgeries modal.
   */
  const handleListOfSurgeriesClose = () => {
    setIsListOfSurgeriesOpen(false)
    setSelectedTray(null)
  }

  /**
   * Navigates to view details of the selected tray.
   * @param tray The tray item for which details are to be viewed.
   */
  const handleViewTrayDetails = (tray: ItemResponse) => {
    if (tray.barcode) {
      setLastViewedTrayId(tray.barcode)
      navigate(`./${tray.barcode}`)
    }
  }

  const handleViewDetailsClick = (tray: ItemResponse) => {
    handleViewTrayDetails(tray)
  }

  const handleEditTrayStatusClick = (tray?: ItemResponse) => {
    if (tray) {
      setSelectedTray({
        trayID: `${tray.id}`,
        trayItem: tray,
      })
    }
    setIsEditTrayStatusModalOpen((prev) => !prev)
  }

  const handleEditTrayStatusChange = async (status: TrayStatus) => {
    setIsUpdatingTrayStatus(true)
    const trayItem = selectedTray?.trayItem

    if (!selectedTray || !trayItem) {
      return
    }

    const trayDetails = extractTrayJSONData(selectedTray.trayItem)

    try {
      const statusDetails: TrayStatusDetails = {
        ...(trayDetails?.statusDetails || {}),
        isRejected: status === 'rejected',
        isApproved: status === 'approved',
        lastUpdatedBy: user?.email,
        lastUpdatedAt: new Date().toISOString(),
      }

      const newProductDetails = JSON.stringify({
        ...trayDetails,
        status,
        ...(status === 'unAssigned'
          ? { surgeryId: undefined, surgeryDetails: undefined }
          : {}),
        statusDetails,
      })

      const { subItems, ...newTrayItem } = trayItem

      const editResult = await editTrayItem({
        ...newTrayItem,
        productDetails: newProductDetails,
      })

      if (!editResult) {
        throw new Error('Failed to edit tray item.')
      }

      const updatedTray = { ...trayItem, productDetails: newProductDetails }

      setTrays((prev) => {
        const newTrays = prev.map((tray) => {
          if (tray.id === trayItem.id) {
            return updatedTray
          }
          return tray
        })

        return newTrays
      })

      refetch()

      handleEditTrayStatusClick()
    } catch (error) {
      console.error(error)
    } finally {
      setIsUpdatingTrayStatus(false)
    }
  }

  const handleViewRejectionDetails = (rejectionDetails: TrayStatusDetails) => {
    setRejectionDetails(rejectionDetails)
    setIsRejectionDetailsModalOpen(true)
  }

  const handleRejectionDetailsModalClose = () => {
    setIsRejectionDetailsModalOpen(false)
    setRejectionDetails(null)
  }

  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement>,
    row: MappedTrays
  ) => {
    if (!event.defaultPrevented) {
      const tray = trays.find((t) => t.barcode === row.trayId)
      if (tray) {
        handleViewDetailsClick(tray)
      }
    }
  }

  /**
   * Groups trays by case for organized display and sorts by surgery date
   * @returns Object containing case groups and unassigned trays
   */
  const groupTraysByCase = (traysToGroup: ItemResponse[]) => {
    const caseGroups: Record<string, ItemResponse[]> = {}
    const unassignedTrays: ItemResponse[] = []
    const caseMetadata: Record<
      string,
      { surgeryDate: string | null; caseNumber: string | null }
    > = {}

    traysToGroup.forEach((tray) => {
      const trayDetails = extractTrayJSONData(tray)
      const surgery = trayDetails?.surgeryDetails
      const hasCaseNumber = surgery?.surgicalCase?.number
      const surgeryId = trayDetails?.surgeryId

      if (surgeryId && (hasCaseNumber || surgery?._id)) {
        const caseKey = `${surgeryId}-${hasCaseNumber || surgery?._id}`
        if (!caseGroups[caseKey]) {
          caseGroups[caseKey] = []

          // Store metadata for sorting
          const surgeryDateTime = surgery?.procedures
            ?.map((proc: Procedures) => proc.dateTime)
            .filter(Boolean)
            .sort(
              (a: string, b: string) =>
                new Date(a).getTime() - new Date(b).getTime()
            )[0]

          caseMetadata[caseKey] = {
            surgeryDate: surgeryDateTime || null,
            caseNumber: surgery?.surgicalCase?.number || null,
          }
        }
        caseGroups[caseKey].push(tray)
      } else {
        unassignedTrays.push(tray)
      }
    })

    // Sort case keys by surgery date
    const sortedCaseKeys = Object.keys(caseGroups).sort((a, b) => {
      const dateA = caseMetadata[a].surgeryDate
        ? new Date(caseMetadata[a].surgeryDate!)
        : null
      const dateB = caseMetadata[b].surgeryDate
        ? new Date(caseMetadata[b].surgeryDate!)
        : null
      const now = new Date()

      // If both dates exist
      if (dateA && dateB) {
        const aIsFuture = dateA > now
        const bIsFuture = dateB > now

        // If one is future and one is past
        if (aIsFuture && !bIsFuture) return -1
        if (!aIsFuture && bIsFuture) return 1

        // If both are future, closest first
        if (aIsFuture && bIsFuture) return dateA.getTime() - dateB.getTime()

        // If both are past, most recent first
        return dateB.getTime() - dateA.getTime()
      }

      // If only one date exists, prioritize the one with a date
      if (dateA && !dateB) return -1
      if (!dateA && dateB) return 1

      // If neither has a date, sort by case number
      return (caseMetadata[a].caseNumber || '').localeCompare(
        caseMetadata[b].caseNumber || ''
      )
    })

    // Create a new sorted object
    const sortedCaseGroups: Record<string, ItemResponse[]> = {}
    sortedCaseKeys.forEach((key) => {
      sortedCaseGroups[key] = caseGroups[key]
    })

    return { caseGroups: sortedCaseGroups, unassignedTrays }
  }

  /**
   * Creates a tray card renderer function bound to the current context
   * @returns A function that renders a tray card
   */
  const createTrayCardRenderer = () => {
    return (tray: ItemResponse) => {
      const trayDetails = extractTrayJSONData(tray)
      const facilityTrayImg = require('assets/icons/images/facility-tray.jpg')
      const consignedTrayImg = require('assets/icons/images/consigned-tray.jpg')
      const loanerTrayImg = require('assets/icons/images/loaner-tray.jpg')
      const trayImg =
        trayDetails?.trayCategory === 'facility'
          ? facilityTrayImg
          : trayDetails?.trayCategory === 'consigned'
          ? consignedTrayImg
          : loanerTrayImg

      const trayStatusDisplay = getTrayStatusDisplay(trayDetails?.status)
      const isSPD = user?.roles?.includes('MGAT_SPD')
      const isRep = user?.roles?.includes('MGAT_REP')
      const isNurse =
        user?.roles?.includes('MGAT_NURSE') &&
        !user?.roles?.includes('MGAT_SPD')
      const shouldBeAllowedToEditTrayStatus = true

      // Add check for rep notification status
      const isRepNotified = ['readyForPickup', 'rejected'].includes(
        trayDetails?.status || ''
      )
      const isRejected = trayDetails?.status === 'rejected'

      return (
        <Card
          className="tray-card"
          key={tray.id}
          id={`tray-card-${tray.barcode}`}
        >
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <div
                  className={`tray-icon-container ${trayDetails?.trayCategory}`}
                >
                  <img src={trayImg} alt="Tray Icon" className="tray-icon" />
                </div>
              </Grid>
              <Grid item xs={12} sm={9}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Typography variant="h6">{tray.barcode}</Typography>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                  >
                    <Chip
                      label={trayStatusDisplay}
                      className={`status-chip ${trayDetails?.status}`}
                    />
                    {isRejected && trayDetails?.statusDetails && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        sx={{ mt: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleViewRejectionDetails(
                            trayDetails?.statusDetails || {}
                          )
                        }}
                      >
                        View Rejection Details
                      </Button>
                    )}
                    {isRejected && isRep && (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        sx={{ mt: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleFixRejectedTray(tray)
                        }}
                      >
                        Fix Rejected Tray
                      </Button>
                    )}
                    {isRepNotified && (
                      <Chip
                        label="Rep Notified ✅"
                        size="small"
                        className="rep-notified-chip"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                </Box>

                <Typography variant="body2" color="textSecondary">
                  <strong>Vendor:</strong> {tray.companyName}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  <strong>Created:</strong>{' '}
                  {dayjs(tray.createdAt).format('MM/DD/YYYY h:mm A')}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  <strong>Location:</strong>{' '}
                  {isLoadingLocations
                    ? 'Loading...'
                    : findLocation(tray?.locationId as number)?.name}
                </Typography>

                <Box mt={2} display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleViewTrayDetails(tray)}
                    startIcon={<ReadMore />}
                  >
                    Details
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleAssignToSurgeryClick(tray)
                    }}
                    startIcon={<Person />}
                  >
                    {trayDetails?.surgeryId ? 'Reassign' : 'Assign'}
                  </Button>

                  {shouldBeAllowedToEditTrayStatus && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEditTrayStatusClick(tray)
                      }}
                      startIcon={<Create />}
                    >
                      Edit Status
                    </Button>
                  )}

                  {trayDetails?.status === 'readyToFill' && isSPD && (
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleLoadTray(tray)
                      }}
                    >
                      Load Tray
                    </Button>
                  )}

                  {(isSPD || isNurse) && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleChooseTrayLocationModalOpen(tray)
                      }}
                      startIcon={<Room />}
                    >
                      Set Location
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )
    }
  }

  const toggleCaseExpansion = (caseId: string) => {
    setExpandedCases((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(caseId)) {
        newSet.delete(caseId)
      } else {
        newSet.add(caseId)
      }
      return newSet
    })
  }

  /**
   * Creates a case group renderer function bound to the current context
   * @returns A function that renders a case group accordion
   */
  const createCaseGroupRenderer = (
    renderTrayCard: (tray: ItemResponse) => JSX.Element
  ) => {
    return (caseId: string, caseTrays: ItemResponse[]) => {
      if (caseTrays.length === 0) return null

      const firstTray = caseTrays[0]
      const trayDetails = extractTrayJSONData(firstTray)
      const surgery = trayDetails?.surgeryDetails
      const isExpanded = expandedCases.has(caseId)
      const surgeryDateTime = trayDetails?.surgeryDetails?.procedures
        ?.map((proc: Procedures) => proc.dateTime)
        .filter(Boolean)
        .sort(
          (a: string, b: string) =>
            new Date(a).getTime() - new Date(b).getTime()
        )[0]
      const showPatientDetails = !isRep

      // Get all unique statuses within this case group
      const statusCounts: { [key: string]: number } = {}
      caseTrays.forEach((tray) => {
        const details = extractTrayJSONData(tray)
        const status = details?.status || 'unknown'
        if (statusCounts[status]) {
          statusCounts[status] += 1
        } else {
          statusCounts[status] = 1
        }
      })

      return (
        <Accordion
          key={caseId}
          expanded={isExpanded}
          onChange={() => toggleCaseExpansion(caseId)}
          className="case-accordion"
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box width="100%">
              <Box
                display="flex"
                flexDirection={{ xs: 'column-reverse', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={1}
              >
                <Typography variant="body2">
                  <strong>Case Number:</strong> {surgery?.surgicalCase?.number}{' '}
                  - {caseTrays.length}{' '}
                  {caseTrays.length === 1 ? 'Tray' : 'Trays'}
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: { xs: 'center', sm: 'flex-end' },
                  }}
                >
                  <Chip
                    icon={<CalendarToday fontSize="small" />}
                    label={
                      surgeryDateTime
                        ? dayjs(surgeryDateTime).format('MM/DD/YYYY h:mm A')
                        : 'No date'
                    }
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>

              <Box mt={1}>
                <Typography variant="body2">
                  <strong>Room:</strong>{' '}
                  {surgery?.visit?.location?.room || 'Not assigned'}
                </Typography>

                {showPatientDetails && (
                  <>
                    <Typography variant="body2">
                      <strong>Patient:</strong>{' '}
                      {surgery?.patient
                        ? `${
                            surgery.patient.lastName
                          }, ${surgery.patient.firstName?.charAt(0)}`
                        : 'Patient name not available'}
                    </Typography>

                    <Typography variant="body2">
                      <strong>DOB:</strong> {surgery?.patient?.dateOfBirth}
                    </Typography>

                    <Typography variant="body2">
                      <strong>MRN:</strong> {surgery?.patient?.idMR}
                    </Typography>
                  </>
                )}

                <Typography variant="body2">
                  <strong>Surgeon:</strong>{' '}
                  {surgery && surgery.visit?.attendingProvider
                    ? formatSurgeryProviderName(surgery)
                    : trayDetails?.selectedSurgeon || 'Unassigned'}
                </Typography>

                {/* Status summary for case */}
                <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    <strong>Tray Status:</strong>
                  </Typography>
                  {Object.entries(statusCounts).map(([status, count]) => (
                    <Chip
                      key={status}
                      size="small"
                      label={`${getTrayStatusDisplay(
                        status as TrayStatus
                      )} (${count})`}
                      className={`status-chip-small ${status}`}
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              {caseTrays.map((tray) => renderTrayCard(tray))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )
    }
  }

  return {
    isAddTrayModalOpen,
    setIsAddTrayModalOpen,
    handleAddTrayModalOpen,
    handleAddTrayModalClose,
    locations,
    locationsWithParents,
    isLoadingLocations,
    findLocation,
    isDetailsModalOpen,
    setIsDetailsModalOpen,
    handleDetailsModalClose,
    handleAssignToSurgeryClick,
    handleListOfSurgeriesClose,
    trayTableBodyCells,
    trayTableHeaderCells,
    getTrayStatusDisplay,
    handleAddTrayClick,
    isEditTrayStatusModalOpen,
    handleEditTrayStatusClick,
    handleEditTrayStatusChange,
    selectedTrayCategory,
    isRejectionDetailsModalOpen,
    setIsRejectionDetailsModalOpen,
    rejectionDetails,
    handleRejectionDetailsModalClose,
    handleRowClick,
    handleLoadTray,
    isShowChooseTrayLocationModalVisible,
    setIsShowChooseTrayLocationModalVisible,
    handleChooseTrayLocationModalOpen,
    handleChooseTrayLocationModalClose,
    handleViewDetailsClick,
    groupTraysByCase,
    createTrayCardRenderer,
    createCaseGroupRenderer,
    expandedCases,
    toggleCaseExpansion,
    isUpdatingTrayStatus,
    handleViewTrayDetails,
    lastViewedTrayId,
    setLastViewedTrayId,
    scrollToTray,
    handleFixRejectedTray,
  }
}

export default useTrayManagementLogic
